import {
  Directive,
  EventEmitter,
  HostBinding,
  HostListener,
  Output,
} from '@angular/core';

@Directive({
  selector: '[appDragDrop]',
  standalone: true,
})
export class DragDropDirective {
  @Output() filesDropped = new EventEmitter<FileList>();
  @Output() dragStateChange = new EventEmitter<boolean>();

  @HostBinding('class.drag-over')
  isDragOver = false;

  /**
   * dragenter/dragleave fire for every child element the cursor crosses, not just the
   * host. Counting enters minus leaves keeps the highlight steady and — crucially for
   * Firefox — stops a child's dragleave from clearing the state right before the drop.
   */
  private dragDepth = 0;

  @HostListener('dragenter', ['$event'])
  onDragEnter(event: DragEvent): void {
    // Firefox only treats an element as a valid drop target once dragenter is cancelled.
    event.preventDefault();
    event.stopPropagation();

    this.dragDepth += 1;
    this.setDragOver(true);
  }

  @HostListener('dragover', ['$event'])
  onDragOver(event: DragEvent): void {
    // Cancelling dragover is what actually enables the drop across every browser.
    event.preventDefault();
    event.stopPropagation();

    // Without an explicit copy effect Firefox refuses the drop and reverts the file.
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'copy';
    }

    this.setDragOver(true);
  }

  @HostListener('dragleave', ['$event'])
  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    this.dragDepth = Math.max(0, this.dragDepth - 1);
    if (this.dragDepth === 0) {
      this.setDragOver(false);
    }
  }

  @HostListener('drop', ['$event'])
  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    this.dragDepth = 0;
    this.setDragOver(false);

    if (event.dataTransfer?.files?.length) {
      this.filesDropped.emit(event.dataTransfer.files);
    }
  }

  private setDragOver(next: boolean): void {
    if (this.isDragOver === next) {
      return;
    }
    this.isDragOver = next;
    this.dragStateChange.emit(next);
  }
}
